import type { FilterConfig, FilterState, TimeRangePreset } from '@bastion/list-view';
import { ptOffsetMinutesAtInstant } from '@bastion/data';

function subHours(d: Date, h: number): Date {
  return new Date(d.getTime() - h * 60 * 60_000);
}

function subDays(d: Date, days: number): Date {
  return subHours(d, days * 24);
}

export const ML_CREATED_PRESETS: TimeRangePreset[] = [
  {
    id: 'last_1h',
    label: 'Last 1 hour',
    resolve: (now) => ({ from: subHours(now, 1), to: now }),
  },
  {
    id: 'last_6h',
    label: 'Last 6 hours',
    resolve: (now) => ({ from: subHours(now, 6), to: now }),
  },
  {
    id: 'last_12h',
    label: 'Last 12 hours',
    resolve: (now) => ({ from: subHours(now, 12), to: now }),
  },
  {
    id: 'last_1d',
    label: 'Last 1 day',
    resolve: (now) => ({ from: subDays(now, 1), to: now }),
  },
  {
    id: 'last_2d',
    label: 'Last 2 days',
    resolve: (now) => ({ from: subDays(now, 2), to: now }),
  },
  {
    id: 'last_7d',
    label: 'Last 7 days',
    resolve: (now) => ({ from: subDays(now, 7), to: now }),
  },
  {
    id: 'last_30d',
    label: 'Last 1 month',
    resolve: (now) => ({ from: subDays(now, 30), to: now }),
  },
];

const CREATED_PRESET_BY_ID = new Map(ML_CREATED_PRESETS.map((p) => [p.id, p]));

export function ptLocalToIso(local: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim());
  if (!m) return local;
  const [, yy, mm, dd, hh, mi, ss] = m;
  const localAsUtcMs = Date.UTC(+yy, +mm - 1, +dd, +hh, +mi, ss ? +ss : 0);
  let offsetMin = ptOffsetMinutesAtInstant(new Date(localAsUtcMs));
  let utcMs = localAsUtcMs - offsetMin * 60_000;
  // Recompute the offset at the candidate instant. This matters on DST
  // transition days where midnight/noon can have a different PT offset
  // than the user-selected local time.
  for (let i = 0; i < 3; i += 1) {
    const nextOffset = ptOffsetMinutesAtInstant(new Date(utcMs));
    if (nextOffset === offsetMin) break;
    offsetMin = nextOffset;
    utcMs = localAsUtcMs - offsetMin * 60_000;
  }
  const sign = offsetMin >= 0 ? '+' : '-';
  const absMin = Math.abs(offsetMin);
  const offH = String(Math.floor(absMin / 60)).padStart(2, '0');
  const offM = String(absMin % 60).padStart(2, '0');
  return `${yy}-${mm}-${dd}T${hh}:${mi}:${ss ?? '00'}${sign}${offH}:${offM}`;
}

export function ptIsoToLocal(iso: string): string {
  const t = iso.trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00`;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '';
  const offsetMin = ptOffsetMinutesAtInstant(d);
  const ptMs = d.getTime() + offsetMin * 60_000;
  const pt = new Date(ptMs);
  const yy = pt.getUTCFullYear();
  const mm = String(pt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(pt.getUTCDate()).padStart(2, '0');
  const hh = String(pt.getUTCHours()).padStart(2, '0');
  const mi = String(pt.getUTCMinutes()).padStart(2, '0');
  return `${yy}-${mm}-${dd}T${hh}:${mi}`;
}

export function createdTimeRangeFilterConfig(icon: FilterConfig['icon']): FilterConfig {
  return {
    kind: 'time-range',
    id: 'created',
    label: 'Created',
    icon,
    presets: ML_CREATED_PRESETS,
    timezoneLabel: 'PT',
    parseLocalDateTime: ptLocalToIso,
    formatLocalDateTime: ptIsoToLocal,
  };
}

export function resolveCreatedRange(
  state: FilterState,
  now: Date = new Date(),
): { from?: Date; to?: Date } | null {
  const f = state.filters.find((x) => x.attributeId === 'created' && x.operator === 'is');
  if (!f || f.values.length === 0) return null;
  if (f.values.length === 1) {
    const preset = CREATED_PRESET_BY_ID.get(f.values[0]?.trim() ?? '');
    if (!preset) return null;
    return preset.resolve(now);
  }
  const fromRaw = f.values[0]?.trim() ?? '';
  const toRaw = f.values[1]?.trim() ?? '';
  const from = fromRaw ? new Date(fromRaw) : undefined;
  const to = toRaw ? new Date(toRaw) : undefined;
  return {
    from: from && !Number.isNaN(from.getTime()) ? from : undefined,
    to: to && !Number.isNaN(to.getTime()) ? to : undefined,
  };
}

export function matchesCreatedRange(createdAt: string | null | undefined, range: { from?: Date; to?: Date } | null): boolean {
  if (!range) return true;
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;
  if (range.from && t < range.from.getTime()) return false;
  if (range.to && t > range.to.getTime()) return false;
  return true;
}
